/**
 * Protected Route Component
 *
 * Wraps routes that require authentication and optionally specific roles.
 * Shows a password change modal if the user must change their password.
 */

import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks'
import type { UserRole } from '../types'
import { PasswordChangeModal } from './PasswordChangeModal'

interface ProtectedRouteProps {
  /** The component to render if authenticated */
  children: React.ReactNode
  /** Required roles (if any role matches, access is granted) */
  requiredRoles?: UserRole[]
  /** Redirect path if not authenticated (default: /) */
  redirectTo?: string
}

export function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/',
}: ProtectedRouteProps): React.ReactElement {
  const { t } = useTranslation('common')
  const { isAuthenticated, isLoading, user, clearPasswordMustChange } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50 dark:bg-stone-950">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-lime-500" />
          <p className="mt-4 text-sm text-stone-600 dark:text-stone-400">{t('actions.loading')}</p>
        </div>
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />
  }

  // Check role requirements if specified
  if (requiredRoles && requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.includes(user.role)
    if (!hasRequiredRole) {
      // User is authenticated but doesn't have required role
      return <Navigate to="/unauthorized" replace />
    }
  }

  // Check if user must change password
  if (user?.passwordMustChange) {
    return (
      <>
        {children}
        <PasswordChangeModal onPasswordChanged={clearPasswordMustChange} />
      </>
    )
  }

  return <>{children}</>
}

/**
 * Higher-order component for admin-only routes
 */
export function AdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return <ProtectedRoute requiredRoles={['evidenx_admin', 'org_admin']}>{children}</ProtectedRoute>
}

/**
 * Higher-order component for platform-admin-only routes
 */
export function PlatformAdminRoute({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  return <ProtectedRoute requiredRoles={['evidenx_admin']}>{children}</ProtectedRoute>
}
